// Grants (epic #1259 slice 1) — an AUDITED entitlement override.
//
// Before this, the only way to lift an account above what its tier + seats buy
// was `accounts.unlimited_fleet`: a boolean nothing in the codebase ever wrote
// (no route, no MCP verb, no seed — only hand-written SQL against prod), with no
// issuer, no reason, no expiry and no revocation. It was never actually set on a
// single row, in prod or in a seed, and it was invisible by construction. "If it
// isn't logged, it didn't happen" (`docs/idea/principles.md` § Audit), so an
// override that leaves no record of WHO granted it, to WHOM, WHY, and UNTIL WHEN
// is not something we can keep selling capacity on — the column is DELETED in
// the same change that introduces this table (no dual-read, no legacy path:
// `docs/idea/principles.md` § Anti-patterns).
//
// A grant carries all four: a SUBJECT (who it lifts), an ISSUER
// (`grantedByUserId` + {@link GrantSource}), a REASON, and a lifetime
// (`expiresAt` / `revokedAt`). It is additive on top of the paid entitlement —
// never a replacement for it — so the tier math in `@developerz/billing` stays
// the source of truth for what money buys, and a grant only ever says "and this
// much more, because <reason>".
//
// Pure — no I/O. The DB write boundary (`@developerz/db` `queries/grants.ts`) and
// any future transport (an operator MCP verb, a sponsor flow) share the shape
// predicates here, so a malformed grant cannot land through either.

import { z } from 'zod';

/**
 * WHOM a grant lifts. Text + Zod-at-read (D3 no-pgEnum stance, same as
 * `RunnerOwnerKind`), with a DB `check` keeping the vocabulary closed.
 *
 * TWO members, and they lift two different things:
 *
 *   - `account` — an `accounts.id`. The org-wide axis: every seat / box / CI
 *     gate reads `getAccountSeatState`, which folds this kind's grants into the
 *     tier math, so an `account` grant raises what THAT ORG may run.
 *   - `user` — a `users.id`. The PERSON axis (epic #1259, fleet-enforcement
 *     slice): a person's own boxes (`runners.owner_kind = 'user'`) stop
 *     occupying any org's paid enrollment budget, and their own concurrency
 *     ceiling follows them into every org they belong to. It lifts the OWNER,
 *     never a tenant — a `user` grant may not widen which ACCOUNTS a box claims
 *     from (that stays `resolveCodingScope`'s membership fence), only how much
 *     its owner may run inside the tenancy they already have.
 *
 * The `user` axis landed WITH its consumers, not before them — it was left out
 * of the table's first cut for exactly that reason ("no abstractions before
 * consumers", `docs/idea/principles.md`). Which KINDS each subject admits is
 * itself a closed rule ({@link grantSubjectAdmitsKind}): a subject that cannot
 * consume a kind must not be able to store it, or an operator ends up believing
 * they granted capacity no gate anywhere honours.
 */
export const GrantSubjectKind = {
  Account: 'account',
  User: 'user',
} as const;
export const GrantSubjectKindSchema = z.enum(GrantSubjectKind);
export type GrantSubjectKind = z.infer<typeof GrantSubjectKindSchema>;

/**
 * WHAT a grant lifts. Text + Zod-at-read; the DB pins the vocabulary AND the
 * amount's shape per kind (`grants_amount_ck`), because both discriminator and
 * amount are read back through {@link GrantSchema}.
 *
 *   - `unlimited_fleet` — uncapped `aiDevs` + `maxQueueDepth`. The ONLY source
 *     of uncapped capacity on the platform since the unaudited
 *     `accounts.unlimited_fleet` column was deleted. Carries NO amount (there is
 *     no "how much" for a flag), which is exactly what {@link grantShapeValid}
 *     enforces.
 *   - `concurrency`     — extra ai-dev slots that cost nothing. Adds to `seats`
 *     in the entitlement math, so the account runs more work in parallel WITHOUT
 *     the paid seats that would normally buy it. It SCALES a fleet and never
 *     CREATES one: on a tier that has none it still resolves to `aiDevs: 0`, so
 *     a routine concurrency grant cannot silently hand out a fleet.
 *   - `boxes`           — extra ENROLLMENT budget, on top of
 *     `aiDevs × SERVERS_PER_DEV`. Distinct from `concurrency` on purpose: this
 *     lets a project attach more machines without burning a paid slot, which is
 *     the sponsor case (someone else's hardware, someone else's keys — we are
 *     not paying for the capacity, so we should not be charging for it either).
 *     It is also FLEET-BEARING (arc 101 slice 03; `ai_devs` below is the other
 *     such kind): on a non-fleet tier its PRESENCE induces `FREE_FLEET_AI_DEVS`
 *     ai-dev spots, so the
 *     machines it pays for actually receive work instead of idling — the AMOUNT
 *     stays additive on every tier, so the four-machine free-fleet promise is
 *     issued as `boxes: 1` (`docs/idea/billing.md` § "Free fleet grant").
 *   - `ci_slots`        — extra CONCURRENT-CI capacity. The CI ceiling is
 *     strictly derived today (`aiDevs × CI_BOXES_PER_DEV`), so the only way to
 *     run more parallel CI is to buy more ai-dev seats — wrong for a customer
 *     who has scaled ai-devs already and wants more GitHub Actions parallelism
 *     on the same seat count, and wrong for a sponsored project we want to hand
 *     free CI capacity to. Additive, absolute (slots, not a ratio).
 *   - `ai_devs`         — N FREE AI-DEV SEATS (#3630): everything a paid seat
 *     buys, gifted. Adds N to `aiDevs` on EVERY tier on top of what money bought,
 *     and on a non-fleet tier it is fleet-bearing AND turns the review product on
 *     — the "friends get two free devs on their personal account" story needs
 *     PRs reviewed and merged, not just machines that receive work. It is THE
 *     documented way to grant devs: before it, "N free devs on `free`" was two
 *     rows (`boxes: 1` to induce the fleet + `concurrency: N-1` to scale it) with
 *     review still off. Admitted for a `user` subject too — the personal axis —
 *     and there it lifts ONLY the person's own `kind = 'user'` account
 *     (`@developerz/billing` `grantedAiDevsFor`); org use stays paid.
 */
export const GrantKind = {
  UnlimitedFleet: 'unlimited_fleet',
  UnlimitedSpend: 'unlimited_spend',
  Concurrency: 'concurrency',
  Boxes: 'boxes',
  CiSlots: 'ci_slots',
  AiDevs: 'ai_devs',
} as const;
export const GrantKindSchema = z.enum(GrantKind);
export type GrantKind = z.infer<typeof GrantKindSchema>;

/**
 * Which {@link GrantKind}s each {@link GrantSubjectKind} can actually be lifted
 * by — the closed subject↔kind matrix, enforced at the write boundary
 * (`createGrant`) AND by `grants_subject_kind_kind_ck`.
 *
 * It exists because the two subjects consume DIFFERENT gates:
 *
 *   - `account` admits every kind. `entitlements()` is computed per account, and
 *     `enrollmentBoxBudget` / `ciCeiling` are account ceilings, so every kind has
 *     a place to land.
 *   - `user` admits `unlimited_fleet`, `concurrency` and `ai_devs` ONLY — the
 *     kinds a person-axis gate reads. The first two feed
 *     {@link personalFleetCeiling} (the owner's concurrency ceiling, carried into
 *     every org they belong to; `unlimited_fleet` additionally takes the owner's
 *     personal boxes out of every org's enrollment count). `ai_devs` is read by a
 *     DIFFERENT gate: `getAccountSeatState` (`@developerz/db`) folds the OWNER
 *     memberships' `user`-subject `ai_devs` grants into the account's seat state
 *     and `entitlements()` honours that total only for a `kind = 'user'` account
 *     — so a personal grant lifts the person's own account and no org's. `boxes`
 *     and `ci_slots` are ACCOUNT ceilings — the 402 is decided per account and the
 *     CI ceiling is derived per account — so a `user` row of either kind would be
 *     storable and unenforceable: capacity an operator believes they granted and
 *     nobody honours. That is the exact failure mode the deleted
 *     `accounts.unlimited_fleet` column had, and refusing it at the boundary is
 *     the "refuse loudly, never silently degrade to a laxer default" rule from
 *     `docs/idea/principles.md` § Anti-patterns.
 *
 * Frozen + exhaustive over `GrantSubjectKind` by construction (`Record<…>`), so
 * a third subject cannot be added without deciding what it may be granted.
 */
export const GRANT_SUBJECT_KINDS: Readonly<Record<GrantSubjectKind, readonly GrantKind[]>> = {
  [GrantSubjectKind.Account]: [
    GrantKind.UnlimitedFleet,
    GrantKind.UnlimitedSpend,
    GrantKind.Concurrency,
    GrantKind.Boxes,
    GrantKind.CiSlots,
    GrantKind.AiDevs,
  ],
  [GrantSubjectKind.User]: [GrantKind.UnlimitedFleet, GrantKind.Concurrency, GrantKind.AiDevs],
};

/**
 * May this subject kind be lifted by this grant kind? The pure predicate over
 * {@link GRANT_SUBJECT_KINDS} — the exact twin of {@link grantShapeValid}: shared
 * by the DB write and any transport so neither restates half the rule, with
 * `grants_subject_kind_kind_ck` as the backstop under both.
 */
export function grantSubjectAdmitsKind(subjectKind: GrantSubjectKind, kind: GrantKind): boolean {
  return GRANT_SUBJECT_KINDS[subjectKind].includes(kind);
}

/**
 * WHO issued it — the accountability axis. `grantedByUserId` says which human,
 * this says on whose authority:
 *
 *   - `operator` — a platform admin (admin.developerz.ai). We are giving away
 *     our own capacity, deliberately: a dogfood org, a design partner, an
 *     apology for an incident.
 *   - `sponsor`  — a user donating capacity to a project that brings its OWN
 *     servers and keys (the donations lane, `docs/idea/donations.md`). The
 *     platform is not paying for what this unlocks, which is why a sponsor may
 *     issue `boxes`/`ci_slots` without an operator in the loop.
 *
 * Text + Zod-at-read, DB-checked like the rest. Not derivable from
 * `grantedByUserId` (a platform admin may also sponsor a project), so it is
 * recorded rather than inferred.
 */
export const GrantSource = {
  Operator: 'operator',
  Sponsor: 'sponsor',
} as const;
export const GrantSourceSchema = z.enum(GrantSource);
export type GrantSource = z.infer<typeof GrantSourceSchema>;

/**
 * Ceiling on a grant's `reason`. It is an audit sentence a human reads in a
 * grant listing ("design partner through the pilot, #1259"), not a document —
 * and it is written by an operator through a transport, so a bound belongs at
 * the write boundary rather than in a reviewer's head. Mirrors
 * `RUNNER_SYSTEM_PROMPT_MAX_CHARS`'s posture: enforced by the schema AND by a
 * DB `check`, never just a UI hint.
 */
export const GRANT_REASON_MAX_CHARS = 500;

/**
 * A NON-EMPTY stated reason at the write boundary. There is no null and no empty
 * string: a grant nobody explained is a capacity increase with no story, which is
 * precisely the state the deleted `accounts.unlimited_fleet` column was in and
 * the reason this table exists. Trimmed, so whitespace cannot pass for an
 * explanation.
 */
export const GrantReasonSchema = z.string().trim().min(1).max(GRANT_REASON_MAX_CHARS);

/**
 * Ceiling on a grant's `subjectId`. The subject is a SOFT reference (no FK — see
 * {@link GrantSchema}), so its SHAPE is the only thing standing between a typo
 * and a grant that lifts nobody forever. Both key shapes it has to cover are far
 * under it (an `acc_`-prefixed account id, a uuid), so the bound is a sanity
 * fence rather than a fit.
 *
 * Stated HERE and enforced by `grants_subject_id_ck` — one number, both layers,
 * exactly like {@link GRANT_REASON_MAX_CHARS}. A Zod bound looser than the DB's
 * is worse than none: the write passes validation and then fails at the driver
 * with a constraint name instead of a field error.
 */
export const GRANT_SUBJECT_ID_MAX_CHARS = 128;

/**
 * Ceiling on a quantity grant's `amount`, every quantity kind alike. Stated HERE
 * and enforced by `grants_amount_ck` — one number, both layers, exactly like
 * {@link GRANT_REASON_MAX_CHARS}.
 *
 * The failure it names (#3630): an operator issuing `ai_devs: 2` to a friend and
 * typing `2000`. Nothing downstream would refuse it — `entitlements()` would
 * honour 2,000 concurrent ai-devs, a 6,000-machine enrollment budget and a
 * 100,000-task queue for a free personal account, all on an audited row that
 * LOOKS deliberate. Every real issuance is a handful (the stories: 2-4 per friend,
 * 6 for the dogfood org); the largest paying fleet holds 10 seats. Uncapped
 * capacity has its own kind (`unlimited_fleet`), so a number this large is never
 * what the operator meant.
 */
export const GRANT_AMOUNT_MAX = 1000;

/**
 * The amount a `concurrency` / `boxes` / `ci_slots` / `ai_devs` grant carries. A
 * POSITIVE integer — `0` is not a grant (it lifts nothing and would still occupy
 * a live row in every resolve), and a fraction has no meaning for slots, boxes,
 * machines or seats — bounded at {@link GRANT_AMOUNT_MAX}. A grant that should
 * stop counting is REVOKED, never zeroed.
 */
export const GrantAmountSchema = z.number().int().positive().max(GRANT_AMOUNT_MAX);

/**
 * The kind+amount pair as a unit — the one invariant both the DB write and any
 * future transport gate on. Structural (not the whole {@link Grant}) so a caller
 * can vet an input before it has an id or a timestamp.
 */
export type GrantShape = {
  kind: GrantKind;
  /** Null for `unlimited_fleet`; a positive integer for every other kind. */
  amount: number | null;
};

/**
 * Is a kind+amount pair well-formed? `unlimited_fleet` is a FLAG, so it carries
 * no amount (a stray `5` there is a bug — five of what? the resolver would
 * silently ignore it); every other kind is a QUANTITY, so it must name one (a
 * null there resolves to "grant nothing", an override that lifts nothing while
 * looking live in every listing).
 *
 * The exact twin of `runnerOwnershipValid`, and for the same reason: pure, no
 * I/O, so the DB write and the transport share it instead of each restating half
 * the rule. The DB's `grants_amount_ck` is the backstop under it — an app-side
 * predicate a writer forgets to call stops nothing.
 */
/**
 * Is this kind a FLAG (carries no amount) rather than a QUANTITY?
 *
 * Exported because it was not, and both write edges paid for it. This was a
 * closure inside {@link grantShapeValid}, so `apps/api/src/routes/admin.ts` and
 * the MCP `grant` verb each restated the rule as `kind === UnlimitedFleet` and
 * each MISSED `unlimited_spend` — which made that kind unissuable through
 * either door: omit the amount and the edge refuses `amount is required`,
 * supply one and this predicate refuses it as a flag carrying a quantity. A
 * legal kind, present in `GrantKindSchema`, admitted for `account` subjects by
 * `GRANT_SUBJECT_KINDS`, and accepted by `grants_amount_ck` — with no path to
 * the database. Measured on prod 2026-09-04: `grants` holds zero rows of any
 * kind, so nothing was lost, which is the only reason this is a two-line fix
 * rather than a migration.
 *
 * Both edges now call this. A sixth kind decides its side here, once.
 */
export function grantKindIsFlag(kind: GrantKind): boolean {
  return kind === GrantKind.UnlimitedFleet || kind === GrantKind.UnlimitedSpend;
}

export function grantShapeValid(grant: GrantShape): boolean {
  return grantKindIsFlag(grant.kind)
    ? grant.amount === null
    : GrantAmountSchema.safeParse(grant.amount).success;
}

/**
 * May this grant carry `extends_to_owned_orgs`? Only a `user`-subject `ai_devs` grant: no other
 * kind is fenced to a personal account, and an `account` subject already names what it lifts.
 * `grants_extends_to_owned_orgs_ck` is the backstop.
 */
export function grantMayExtendToOwnedOrgs(grant: {
  subjectKind: GrantSubjectKind;
  kind: GrantKind;
}): boolean {
  return grant.subjectKind === GrantSubjectKind.User && grant.kind === GrantKind.AiDevs;
}

export const GrantSchema = z
  .object({
    /** Raw uuid PK — no brand (the id is a DB handle, not a domain value object). */
    id: z.uuid(),
    subjectKind: GrantSubjectKindSchema,
    /**
     * The subject reference: an `accounts.id` (`account`) or a `users.id`
     * (`user`) — which one is decided by {@link subjectKind}, never by the
     * value's shape.
     *
     * A SOFT reference — no FK, deliberately, exactly like `runners.owner_id`.
     * A grant is an audit record of a decision we made, and it must OUTLIVE its
     * subject: "why did this org run 40 boxes for free in July" has to stay
     * answerable after the org is offboarded. A cascade would delete the
     * evidence along with the account; a `set null` would leave a grant pointing
     * at nobody, which is worse than one pointing at a deleted id. So the
     * reference is validated at the WRITE boundary (the transport resolves the
     * subject before it calls) and never enforced by the database.
     *
     * The cost is real and accepted: a resolve for a deleted subject simply
     * matches nothing, and a stale row can never lift an account that no longer
     * exists.
     *
     * Bounded at {@link GRANT_SUBJECT_ID_MAX_CHARS} — the SAME number
     * `grants_subject_id_ck` enforces, so the two layers agree and an
     * over-long id is a field error at the boundary rather than a driver-level
     * constraint violation.
     */
    subjectId: z.string().min(1).max(GRANT_SUBJECT_ID_MAX_CHARS),
    kind: GrantKindSchema,
    /**
     * Null for `unlimited_fleet`, a positive integer otherwise
     * ({@link grantShapeValid}, backed by `grants_amount_ck`).
     */
    amount: GrantAmountSchema.nullable(),
    source: GrantSourceSchema,
    /**
     * The human who issued it (`users.id`). Soft ref for the same reason
     * {@link subjectId} is: the issuer leaving must not erase what they granted.
     */
    grantedByUserId: z.string().min(1),
    /** Why — non-empty, bounded ({@link GrantReasonSchema}). */
    reason: GrantReasonSchema,
    /**
     * When it stops counting on its own. Null = open-ended (the dogfood org
     * case). A grant whose expiry has PASSED is inert but still readable — the
     * audit answer to "what were they running on in July" needs the row.
     */
    expiresAt: z.date().nullable(),
    /**
     * When an operator pulled it early. PARKS the row, never deletes it — same
     * doctrine as `donations.revoked_at`: the reason we granted capacity and the
     * reason we took it back are both part of the record.
     */
    revokedAt: z.date().nullable(),
    /**
     * THE NAMED EXCEPTION to the personal-only fence (owner, 2026-11-02): this `user`-subject
     * `ai_devs` grant also lifts every org its holder OWNS, its amount being the TOTAL across
     * their personal account and those orgs (`@developerz/billing` `grant-seat-pool.ts`). Stated
     * per grant, off by default; a row that should change it is revoked and re-issued.
     */
    extendsToOwnedOrgs: z.boolean(),
    createdAt: z.date(),
  })
  .superRefine((grant, ctx) => {
    if (grant.extendsToOwnedOrgs && !grantMayExtendToOwnedOrgs(grant)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['extendsToOwnedOrgs'],
        message: `only a user-subject ai_devs grant may extend to owned orgs, got ${grant.subjectKind} ${grant.kind}`,
      });
    }
    // The DB says the same thing in `grants_amount_ck`; saying it here too means
    // a drifted row fails LOUD at read instead of resolving as a silent zero.
    if (!grantShapeValid(grant)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message: grantKindIsFlag(grant.kind)
          ? `${grant.kind} carries no amount`
          : `${grant.kind} requires a positive integer amount <= ${GRANT_AMOUNT_MAX}`,
      });
    }
  });
export type Grant = z.infer<typeof GrantSchema>;

/**
 * The minimum a grant must expose to be resolved into entitlement totals. The
 * lifetime columns plus the kind+amount pair — structural, so a projection read
 * (`listActiveGrantsForSubject`) can be resolved without building whole
 * {@link Grant} values the resolver would not look at.
 */
export type GrantEntitlement = GrantShape & {
  expiresAt: Date | null;
  revokedAt: Date | null;
};

/**
 * Is this grant counting RIGHT NOW? Not revoked, and either open-ended or not
 * yet expired.
 *
 * The boundary is deliberate: `expiresAt === now` is EXPIRED, not active. An
 * expiry is the instant the capacity stops, so the half-open window
 * `[createdAt, expiresAt)` is what "until 5pm" means to the person who typed it
 * — and picking the inclusive side would leave a grant lifting an account for
 * one more tick after the moment we told them it would stop.
 *
 * `now` is passed in, never read from the clock here: the caller freezes one
 * instant for the whole resolve, so a list of grants can't be evaluated against
 * two different "now"s mid-pass.
 */
export function grantActive(
  grant: Pick<GrantEntitlement, 'expiresAt' | 'revokedAt'>,
  now: Date,
): boolean {
  if (grant.revokedAt !== null) return false;
  return grant.expiresAt === null || grant.expiresAt.getTime() > now.getTime();
}

/**
 * The resolved totals a set of grants adds to an account's paid entitlement —
 * the shape `@developerz/billing`'s `EntitlementInput` takes. Numbers, never
 * rows: `entitlements()` stays pure and knows nothing about grants, expiry or
 * revocation, so a change to what "active" means can never leak into the tier
 * math.
 */
export type ResolvedGrants = {
  /** Any active `unlimited_fleet` grant — the ONE source of uncapped capacity. */
  unlimited: boolean;
  /** Any active `unlimited_spend` grant — the ONE source of uncapped spend. */
  unlimitedSpend: boolean;
  /** Extra ai-dev slots (summed `concurrency` amounts). */
  concurrency: number;
  /** Extra enrollment budget in machines (summed `boxes` amounts). */
  boxes: number;
  /** Extra concurrent-CI slots (summed `ci_slots` amounts). */
  ciSlots: number;
  /**
   * Free ai-dev seats (summed `ai_devs` amounts, #3630). For an `account` subject
   * this is `EntitlementInput.grantedAiDevs`; for a `user` subject it is the
   * personal total `getAccountSeatState` folds in as `ownerGrantedAiDevs`, and
   * {@link personalFleetCeiling} deliberately does NOT read it.
   */
  aiDevs: number;
};

/** The zero value — what an empty (or wholly inactive) grant set resolves to. */
const NO_GRANTS: ResolvedGrants = {
  unlimited: false,
  unlimitedSpend: false,
  concurrency: 0,
  boxes: 0,
  ciSlots: 0,
  aiDevs: 0,
};

/**
 * Fold a subject's grants into entitlement totals at instant `now`.
 *
 * ADDITIVE and OR-ing: amounts of the same kind SUM (two sponsors funding four
 * boxes each is eight boxes — a max would silently discard one sponsor's gift),
 * and the `unlimited_fleet` flag ORs (one live unlimited grant is unlimited, no
 * matter what else is on the row set). Inactive grants — revoked or expired —
 * contribute NOTHING, which is the whole point of `grantActive` being a
 * predicate rather than a delete: the rows stay for audit and stop counting.
 *
 * An empty list yields the zero value, so a subject with no grants resolves to
 * exactly what it paid for and nothing changes for the overwhelming majority of
 * accounts.
 */
export function resolveGrants(grants: readonly GrantEntitlement[], now: Date): ResolvedGrants {
  const resolved: ResolvedGrants = { ...NO_GRANTS };
  for (const grant of grants) {
    if (!grantActive(grant, now)) continue;
    if (grant.kind === GrantKind.UnlimitedFleet) {
      resolved.unlimited = true;
      continue;
    }
    if (grant.kind === GrantKind.UnlimitedSpend) {
      resolved.unlimitedSpend = true;
      continue;
    }
    // A malformed amount (null / non-positive on a quantity kind) is refused by
    // both `grants_amount_ck` and `GrantSchema`; treating it as 0 here keeps the
    // resolver total rather than throwing on a row that cannot exist.
    const amount = grant.amount ?? 0;
    if (grant.kind === GrantKind.Concurrency) resolved.concurrency += amount;
    else if (grant.kind === GrantKind.Boxes) resolved.boxes += amount;
    else if (grant.kind === GrantKind.CiSlots) resolved.ciSlots += amount;
    else if (grant.kind === GrantKind.AiDevs) resolved.aiDevs += amount;
  }
  return resolved;
}

/**
 * A box OWNER's own concurrency ceiling, folded from their `user`-subject grants
 * (epic #1259, fleet-enforcement slice). The number the assigner's COUNT gate
 * (`admitsConcurrency`, `apps/worker`) takes as the person's half of the ceiling,
 * beside the candidate ACCOUNT's entitled `aiDevs`.
 *
 * `Infinity` for an active `unlimited_fleet` grant — the SAME sentinel
 * `entitlements()` returns for an uncapped account, so the two halves are
 * directly comparable and `Math.max` of them stays `Infinity` rather than
 * degrading to a finite number. Otherwise the summed `concurrency` amounts, and
 * `0` for the overwhelming majority of people, who hold no grants at all — a
 * zero adds nothing to the account ceiling, so an owner with no grant is
 * byte-identical to the pre-grant behaviour.
 *
 * `boxes` / `ciSlots` are deliberately NOT read here: they are ACCOUNT ceilings
 * ({@link GRANT_SUBJECT_KINDS} refuses them on a `user` subject). `aiDevs` IS
 * storable on a `user` subject and is STILL not read here, on purpose (#3630):
 * this ceiling is compared against EVERY account the owner's box claims from,
 * and a personal `ai_devs` grant must lift only the person's own `kind = 'user'`
 * account — "org use is paid". That fold happens in `getAccountSeatState`
 * (`@developerz/db`, over the account's OWNER memberships) and is fenced in
 * `entitlements()` (`@developerz/billing` `grantedAiDevsFor`); reading it here
 * too would hand every org the person belongs to their free seats.
 */
export function personalFleetCeiling(resolved: ResolvedGrants): number {
  return resolved.unlimited ? Number.POSITIVE_INFINITY : resolved.concurrency;
}
